// packages/web-ui/src/components/welcome/EmptyWelcome.tsx
//
// W2-S10 Task 2.1 (AC #2) — top-level container shown when the open-tabs list
// is empty. Wraps `<QuickActions>` + `<RecentList>` and exposes plain props so
// the host (`<TabContentArea>`) stays in charge of state subscriptions.
//
// AC #2 mandates: 3 quick-action cards in fixed order + a "Recent" rail with
// 5 chats and 5 files. Empty rails show a muted placeholder line; never a
// hard error.

import type { JSX } from 'react';
import { QuickActions, type QuickActionIntent } from './QuickActions.js';
import { RecentList, type RecentSession, type RecentFile } from './RecentList.js';

export interface EmptyWelcomeProps {
  /** Sessions surfaced in the left "Recent chats" column (caller slices to 5). */
  recentSessions: ReadonlyArray<RecentSession>;
  /** Files surfaced in the right "Recent files" column (caller slices to 5). */
  recentFiles: ReadonlyArray<RecentFile>;
  /** Loading state for the recent-files fetch. */
  recentFilesStatus: 'idle' | 'loading' | 'ready' | 'error';
  /** Quick-action click handler — the host opens the chat tab pre-seeded with intent. */
  onQuickAction: (intent: QuickActionIntent) => void;
  /** Recent-session row activation. */
  onActivateSession: (sessionId: string) => void;
  /** Recent-file row activation. */
  onActivateFile: (path: string) => void;
  /** Optional test-id. */
  testId?: string;
}

export function EmptyWelcome({
  recentSessions,
  recentFiles,
  recentFilesStatus,
  onQuickAction,
  onActivateSession,
  onActivateFile,
  testId = 'empty-welcome',
}: EmptyWelcomeProps): JSX.Element {
  return (
    <section
      data-testid={testId}
      role="region"
      aria-label="Welcome"
      className="mx-auto flex w-full max-w-3xl flex-col gap-10 p-10"
    >
      <header className="flex flex-col gap-3">
        <span className="text-audi-red text-[11px] font-semibold uppercase tracking-[0.22em]">
          TESSA · Engineering Workspace
        </span>
        <h2 className="text-fg text-4xl font-extrabold leading-[1.05] tracking-[-0.02em]">
          Welcome to TESSA Lean.
        </h2>
        <p className="text-taupe-700 max-w-xl text-base leading-relaxed">
          Describe a change to your SysML knowledge base in natural language —
          accept or reject each proposed entity, ship faster.
        </p>
      </header>

      <QuickActions onAction={onQuickAction} />

      <RecentList
        sessions={recentSessions}
        files={recentFiles}
        filesStatus={recentFilesStatus}
        onActivateSession={onActivateSession}
        onActivateFile={onActivateFile}
      />
    </section>
  );
}
